import { dx, dy, opposite, turnLeft, turnRight, type Direction } from '../../sim/direction';
import type { Intent } from '../../sim/mower';
import type { Simulation } from '../../sim/simulation';

/**
 * Plays a hedge maze match (docs/MAZE_MATCH.md): at every tile center, heads for whichever way on
 * (straight, a quarter turn either side, or back when nothing else is open) is fewest tiles from
 * the plaza along the paths, keeping straight on a tie. Drives the in-game autopilot, the e2e and
 * the whistle's timing. Pass this seat's view of the match (`seatView`), or the match and the seat.
 */
export class MazePilot {
  /** Tiles to the plaza from each tile (Infinity: no way there). */
  private readonly dist: Float64Array;

  constructor(
    private readonly sim: Simulation,
    private readonly seat?: number,
  ) {
    const { grid, mowerSpec } = sim;
    const w = grid.width;
    this.dist = new Float64Array(w * grid.height).fill(Infinity);
    const queue: number[] = [];
    for (const p of sim.level.maze?.plaza ?? []) {
      this.dist[p.y * w + p.x] = 0;
      queue.push(p.y * w + p.x);
    }
    for (let q = 0; q < queue.length; q++) {
      const i = queue[q]!;
      const x = i % w;
      const y = Math.floor(i / w);
      for (const d of [0, 1, 2, 3] as Direction[]) {
        const nx = x + dx(d);
        const ny = y + dy(d);
        if (!grid.inBounds(nx, ny)) continue;
        if (grid.isBlockedFor(nx, ny, mowerSpec.cutPower, mowerSpec.breakPower ?? 0)) continue;
        const j = ny * w + nx;
        if (this.dist[j]! <= this.dist[i]! + 1) continue;
        this.dist[j] = this.dist[i]! + 1;
        queue.push(j);
      }
    }
  }

  /** Tiles from (x, y) to the plaza along the paths (Infinity off the paths). */
  distance(x: number, y: number): number {
    const { grid } = this.sim;
    return grid.inBounds(x, y) ? this.dist[y * grid.width + x]! : Infinity;
  }

  /** At a tile center: the way to face (null to keep going straight). */
  decide(): Intent | null {
    const sim = this.sim;
    const m = this.seat === undefined ? sim.mower : sim.asSeat(this.seat, () => ({ ...sim.mower }));
    if (m.progress !== 0) return null;
    const ahead = (d: Direction) => this.distance(m.x + dx(d), m.y + dy(d));
    let best = m.dir;
    for (const d of [turnLeft(m.dir), turnRight(m.dir)]) if (ahead(d) < ahead(best)) best = d;
    // Nothing ahead or to either side leads on: back the way it came.
    if (!Number.isFinite(ahead(best))) best = opposite(m.dir);
    return best === m.dir ? null : { type: 'face', dir: best };
  }
}
